// because we need dynamic property access combined with fluent interface here:
// builder
//   [mLineTo](...)
//   [sLineTo](...)
//
import { useMemo } from 'react';

import { SvgPathBuilder } from '@ipfabric/utils';
import classNames from 'classnames';
import type {
  ConnectionLinesStyle,
  TreeOrientation,
} from 'components/BoxTree/BoxTreeHocProps';
import type {
  BoxTreeLevelLayoutInfo,
  ChildBoxLayoutInfo,
} from 'components/BoxTree/BoxTreeLevelLayoutInfo';
import { nonEmptyArray } from 'fp-ts';
import type { FC } from 'react';

// Main axis (M): direction of the main line connecting all to-parent and to-child lines together
// (= vertical when children are rendered vertically = one child below other)
// Side axis (S): direction of lines connected to nodes
// (= horizontal when children are rendered vertically)

interface Props {
  /**
   * null makes this component render placeholder div with appropriate side axis size
   */
  layoutInfo: BoxTreeLevelLayoutInfo | null;
  linesStyle?: ConnectionLinesStyle | null;
  orientation: TreeOrientation;
}

const CONNECTION_LINE_LENGTH = 10;
const LINE_THICKNESS = 1;
const SIDE_AXIS_SIZE = 2 * CONNECTION_LINE_LENGTH + LINE_THICKNESS;

/**
 * Get whole-pixel center position of an element of given size (in either direction)
 *
 * Odd positions are clear (e.g. center of height 3 is 1).
 * When size is even, we choose smaller-number pixel position as it looks better
 * (e.g. center of height 4 is 1).
 */
const getCenterPosition = (size: number) =>
  Math.max(0, Math.ceil(size / 2) - 1);

const getChildLineMPosition = (child: ChildBoxLayoutInfo) =>
  child.position + getCenterPosition(child.size);

/**
 * Draw the line structure that represents connection of a parent box to children as a single
 * SVG path to be filled
 *
 * A single filled path provides a smooth shape without unnecessary subpixel rendering
 * or glitches in corners.
 * We decided for this approach, although it is a bit clumsy to draw such abstract line this way,
 * because:
 * - With `stroke` the line is clearer to understand, but it is too tricky to avoid subpixel rendering
 *   as stroke is rendered "centered to the line between pixels".
 * - Composing from multiple rectangles or similar resulted in glitchy corners when zooming in browser.
 */
const getLinePath = (
  layoutInfo: BoxTreeLevelLayoutInfo,
  orientation: TreeOrientation,
  style: ConnectionLinesStyle | null,
): [svgPath: string, mSize: number] => {
  const [mLineTo, sLineTo] =
    orientation === 'vertical'
      ? (['vLineTo', 'hLineTo'] as const)
      : (['hLineTo', 'vLineTo'] as const);

  const builder = new SvgPathBuilder(true);

  const thickness = style?.thickness ?? 1;
  const thicknessHalfF = Math.floor(thickness / 2);
  const thicknessHalfC = Math.ceil(thickness / 2);
  const firstLineMPosition = Math.max(
    0,
    Math.min(
      getCenterPosition(layoutInfo.rootSize),
      getChildLineMPosition(layoutInfo.children[0]),
    ) - thicknessHalfF,
  );

  /**
   * Draw a line for child on position
   *
   * Expects current position at bottom right corner of previous child's line
   * (it means this is not for drawing the first child).
   */
  const drawChildLine = (position: number) => {
    builder[sLineTo](CONNECTION_LINE_LENGTH + thicknessHalfC)
      [mLineTo](position - thicknessHalfF)
      [sLineTo](SIDE_AXIS_SIZE)
      .relative()
      [mLineTo](thickness)
      .absolute();
  };

  builder
    // Start at top left corner of line from parent to first child (PC1)
    .moveTo(
      orientation === 'horizontal' ? firstLineMPosition : 0,
      orientation === 'vertical' ? firstLineMPosition : 0,
    )
    [sLineTo](SIDE_AXIS_SIZE)
    .relative()
    // bottom right corner of PC1
    [mLineTo](thickness)
    .absolute();

  if (layoutInfo.children.length > 1) {
    layoutInfo.children
      .slice(1)
      .map(getChildLineMPosition)
      .forEach(drawChildLine);

    builder
      // move from last child line's bottom right corner to (vertical: bottom left / horizontal: top right) of main line
      [sLineTo](CONNECTION_LINE_LENGTH - thicknessHalfF)
      // move back to the bottom side of PC1
      [mLineTo](firstLineMPosition + thickness);
  }
  builder[sLineTo](0);
  const mSize =
    (layoutInfo.children.length > 1
      ? getChildLineMPosition(nonEmptyArray.last(layoutInfo.children))
      : firstLineMPosition) + thickness;

  return [builder.buildClosed(), mSize];
};

export const BoxTreeLevelConnectionLines: FC<Props> = (props) => {
  const [linePath, mSize] = useMemo(
    () =>
      props.layoutInfo
        ? getLinePath(
            props.layoutInfo,
            props.orientation,
            props.linesStyle ?? null,
          )
        : // placeholder will have 1px main axis size - that's enough, this element has always
          // lower size on that axis than its siblings in the flex, we just need to set proper
          // gap between parent and children.
          [null, 1],
    [props.layoutInfo, props.linesStyle, props.orientation],
  );

  const [width, height] =
    props.orientation === 'vertical'
      ? [SIDE_AXIS_SIZE, mSize]
      : [mSize, SIDE_AXIS_SIZE];

  if (linePath === null) {
    // just render placeholder with required SIDE_AXIS_SIZE to render the tree properly sized immediately
    return (
      <div className='box-tree__connection-lines' style={{ width, height }} />
    );
  }

  return (
    <svg
      className={classNames(
        'box-tree__connection-lines',
        props.linesStyle?.className ?? 'box-tree__connection-lines--default',
      )}
      xmlns='http://www.w3.org/2000/svg'
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role='graphics-symbol'
    >
      <path d={linePath} fill='currentColor' />
    </svg>
  );
};
